import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  Key, 
  RefreshCw, 
  Copy, 
  Check, 
  AlertCircle, 
  Trash2,
  Save,
  Plus,
  Mail,
  User,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { api } from '../../../utils/api';
import UserSelector from './UserSelector';
import './WebFormApiKeys.css';

const WebFormApiKeys = () => {
  const { token } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [newApiKey, setNewApiKey] = useState(null);
  
  const [formData, setFormData] = useState({
    defaultUserId: '',
    emailRecipient: '',
    enabled: true,
    template: 'default'
  });
  const [templates, setTemplates] = useState({});

  useEffect(() => {
    loadApiKeyConfig();
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await api.get('/webform/api-keys/templates');
      setTemplates(response);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const loadApiKeyConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/webform/api-keys');
      console.log('Load API Key Config Response:', response);
      setConfig(response);
      setFormData({
        defaultUserId: response.defaultUserId || '',
        emailRecipient: response.emailRecipient || 'contact@flowai.co.th',
        enabled: response.enabled !== false,
        template: response.template || 'default'
      });
      setNewApiKey(null);
    } catch (error) {
      console.error('Failed to load API key config:', error);
      toast.error('Failed to load API key configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!window.confirm('Generate a new API key? The old key will be invalidated.')) {
      return;
    }

    try {
      setSaving(true);
      // Prepare data - convert empty strings to undefined and defaultUserId to integer
      const payload = {
        defaultUserId: formData.defaultUserId ? parseInt(formData.defaultUserId, 10) : undefined,
        emailRecipient: formData.emailRecipient || undefined,
        enabled: formData.enabled,
        template: formData.template || 'default'
      };
      
      const response = await api.post('/webform/api-keys/generate', payload);
      console.log('Generate API Key Response:', response);
      setNewApiKey(response.apiKey);
      setConfig(response);
      // Update form data with response values
      setFormData({
        defaultUserId: response.defaultUserId || '',
        emailRecipient: response.emailRecipient || 'contact@flowai.co.th',
        enabled: response.enabled !== false,
        template: response.template || 'default'
      });
      toast.success('API key generated successfully! Copy it now - it won\'t be shown again.');
    } catch (error) {
      console.error('Failed to generate API key:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.errors?.[0]?.msg || 
                          error.message || 
                          'Failed to generate API key';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleRotate = async () => {
    if (!window.confirm('Rotate API key? The current key will be invalidated and a new one generated.')) {
      return;
    }

    try {
      setSaving(true);
      const response = await api.post('/webform/api-keys/rotate');
      setNewApiKey(response.apiKey);
      setConfig(response);
      // Update form data with response values
      setFormData({
        defaultUserId: response.defaultUserId || '',
        emailRecipient: response.emailRecipient || 'contact@flowai.co.th',
        enabled: response.enabled !== false,
        template: response.template || 'default'
      });
      toast.success('API key rotated successfully! Copy it now - it won\'t be shown again.');
    } catch (error) {
      console.error('Failed to rotate API key:', error);
      toast.error(error.response?.data?.error || 'Failed to rotate API key');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setSaving(true);
      const response = await api.put('/webform/api-keys', formData);
      setConfig(response);
      toast.success('Configuration updated successfully');
    } catch (error) {
      console.error('Failed to update config:', error);
      toast.error(error.response?.data?.error || 'Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete API key? This will disable web form submissions.')) {
      return;
    }

    try {
      setSaving(true);
      await api.delete('/webform/api-keys');
      await loadApiKeyConfig();
      toast.success('API key deleted successfully');
    } catch (error) {
      console.error('Failed to delete API key:', error);
      toast.error(error.response?.data?.error || 'Failed to delete API key');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  // Determine which API key to display
  let displayApiKey = null;
  if (newApiKey) {
    displayApiKey = newApiKey;
  } else if (config?.hasApiKey) {
    displayApiKey = config.maskedApiKey;
  }

  if (loading) {
    return (
      <div className="webform-api-keys-container">
        <div className="loading-state">
          <RefreshCw className="spinner" />
          <p>Loading API key configuration...</p>
        </div>
      </div>
    );
  }

  // Get current API URL
  const currentApiUrl = window.location.origin;
  
  // Get the actual API key for embed code
  const embedApiKey = newApiKey || (config?.hasApiKey ? 'YOUR_API_KEY_HERE' : 'GENERATE_KEY_FIRST');

  return (
    <div className="webform-api-keys-container">
      <div className="webform-api-keys-content">
        {/* Top Row: API Key and Usage Instructions */}
        <div className="top-row">
          {/* API Key Display */}
          <div className="api-key-card">
            <div className="card-header">
            <h2>API Key</h2>
            {config?.hasApiKey && (
              <div className="key-status">
                <span className={`status-badge ${config.enabled ? 'active' : 'inactive'}`}>
                  {config.enabled ? 'Active' : 'Inactive'}
                </span>
              </div>
            )}
          </div>

          {config?.hasApiKey ? (
            <div className="api-key-display">
              <div className="key-value">
                {displayApiKey && (
                  <code className={newApiKey ? 'full-key' : 'masked-key'}>
                    {displayApiKey}
                  </code>
                )}
                {newApiKey && (
                  <button
                    className="icon-button"
                    onClick={() => handleCopy(newApiKey)}
                    title="Copy key"
                  >
                    {copied ? <Check size={18} className="success" /> : <Copy size={18} />}
                  </button>
                )}
              </div>
              {newApiKey && (
                <div className="new-key-warning">
                  <AlertCircle size={16} />
                  <span>This is your new API key. Copy it now - it won't be shown again!</span>
                </div>
              )}
              {config.lastRotated && (
                <p className="key-meta">Last rotated: {new Date(config.lastRotated).toLocaleString()}</p>
              )}
            </div>
          ) : (
            <div className="no-api-key">
              <AlertCircle size={24} />
              <p>No API key configured. Generate one to enable web form submissions.</p>
            </div>
          )}

          <div className="key-actions">
            {!config?.hasApiKey ? (
              <button
                className="btn btn-primary"
                onClick={handleGenerate}
                disabled={saving}
              >
                <Plus size={18} />
                Generate API Key
              </button>
            ) : (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={handleRotate}
                  disabled={saving}
                >
                  <RefreshCw size={18} />
                  Rotate Key
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  <Trash2 size={18} />
                  Delete Key
                </button>
              </>
            )}
          </div>
        </div>

        {/* Usage Instructions */}
        <div className="instructions-card">
          <div className="card-header">
            <h2>Embedding Instructions</h2>
          </div>
          <div className="instructions-content">
            <h3>Add to Your Website</h3>
            <p>Copy and paste this code into your HTML:</p>
            <div className="code-block">
              <code>
                {`<div id="webform-container" 
     data-tenant-code="${config?.tenantCode || 'YOUR_TENANT_CODE'}" 
     data-api-key="${embedApiKey}"
     data-api-url="${currentApiUrl}"></div>
<script src="${currentApiUrl}/webform-embed.js"></script>
<script>WebFormEmbed.autoInit();</script>`}
              </code>
              <button
                className="copy-code-button"
                onClick={() => handleCopy(`<div id="webform-container" 
     data-tenant-code="${config?.tenantCode || 'YOUR_TENANT_CODE'}" 
     data-api-key="${embedApiKey}"
     data-api-url="${currentApiUrl}"></div>
<script src="${currentApiUrl}/webform-embed.js"></script>
<script>WebFormEmbed.autoInit();</script>`)}
                title="Copy code"
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
            {newApiKey && (
              <div className="embed-note success">
                <Check size={16} />
                <span>Code updated with your new API key!</span>
              </div>
            )}
            {!config?.hasApiKey && (
              <div className="embed-note warning">
                <AlertCircle size={16} />
                <span>Generate an API key first to get the complete embed code</span>
              </div>
            )}
            <p><strong>Template:</strong> The form will use the "{config?.template || 'default'}" template configured below.</p>
          </div>
        </div>
      </div>

      {/* Configuration */}
      <div className="config-card">
        <div className="card-header">
          <h2>Configuration</h2>
        </div>

        <div className="config-form">
            <div className="form-group">
              <label htmlFor="defaultUserId">
                <User size={16} />
                Default User
              </label>
              <UserSelector
                value={formData.defaultUserId}
                onChange={(userId) => setFormData({ ...formData, defaultUserId: userId })}
                disabled={saving}
              />
              <small>User that will own notes created from web form submissions</small>
            </div>

            <div className="form-group">
              <label htmlFor="emailRecipient">
                <Mail size={16} />
                Email Recipient
              </label>
              <input
                type="email"
                id="emailRecipient"
                value={formData.emailRecipient}
                onChange={(e) => setFormData({ ...formData, emailRecipient: e.target.value })}
                placeholder="contact@flowai.co.th"
              />
              <small>Email address to receive web form submission notifications</small>
            </div>

            <div className="form-group">
              <label className="toggle-label">
                <div>
                  <ToggleRight size={16} />
                  Enable Web Form
                </div>
                <button
                  type="button"
                  className={`toggle-button ${formData.enabled ? 'enabled' : 'disabled'}`}
                  onClick={() => setFormData({ ...formData, enabled: !formData.enabled })}
                >
                  {formData.enabled ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                </button>
              </label>
              <small>Enable or disable web form submissions for this tenant</small>
            </div>

            <div className="form-group">
              <label htmlFor="template">
                <Key size={16} />
                Form Template
              </label>
              <select
                id="template"
                value={formData.template}
                onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                className="webform-select"
              >
                {Object.keys(templates).map(templateId => (
                  <option key={templateId} value={templateId}>
                    {templates[templateId]?.name || templateId}
                  </option>
                ))}
              </select>
              {templates[formData.template]?.description && (
                <small>{templates[formData.template].description}</small>
              )}
            </div>

            <button
              className="btn btn-primary"
              onClick={handleUpdate}
              disabled={saving}
            >
              <Save size={18} />
              Save Configuration
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebFormApiKeys;
